import {defineStore} from "pinia";
import {
    AllInvoicesQuery, AllPurchaseInvoicesQuery,
    AthumiService,
    OrderDirection, PaymentMethod,
    SearchSessionsQuery,
    SessionState,
    SessionType
} from "@/api/enterprise-gateway-api.proxy.ts";
import dayjs from "dayjs";
import {useRoute, useRouter} from "vue-router";
import type {ISearchOrganisationQuery} from "@/app/stores/organisation-store.ts";

export class SearchSessionsQueryMapper {
    private static toEnum<E extends string>(value: unknown, enumObj: Record<string, string>): E | undefined {
        if (typeof value !== "string") return undefined;
        const values = Object.values(enumObj) as string[];
        return values.includes(value) ? (value as E) : undefined;
    }

    static toQueryParams(searchSessionsQuery: SearchSessionsQuery): Record<string, string | undefined> {
        return {
            // Only include defined values to keep URL clean
            transactionId: searchSessionsQuery.transactionId || undefined,
            fromDate: searchSessionsQuery.fromDate ? dayjs(searchSessionsQuery.fromDate).format("YYYY-MM-DDHH:mm") : undefined,
            toDate: searchSessionsQuery.toDate ? dayjs(searchSessionsQuery.toDate).format("YYYY-MM-DDHH:mm") : undefined,
            state: searchSessionsQuery.state ?? undefined,
            type: searchSessionsQuery.type ?? undefined,
            service: searchSessionsQuery.service ?? undefined,
            paymentMethod: searchSessionsQuery.paymentMethod || undefined,
            actorIdentifier: searchSessionsQuery.actorIdentifier || undefined,
            actorName: searchSessionsQuery.actorName || undefined,
            pageSize: searchSessionsQuery.pageSize != null ? String(searchSessionsQuery.pageSize) : undefined,
            page: searchSessionsQuery.page != null ? String(searchSessionsQuery.page) : undefined,
            orderDirection: searchSessionsQuery.orderDirection ?? undefined,
            sessionDetail: searchSessionsQuery.sessionDetail ?? undefined
        } as Record<string, string | undefined>;
    }

    static fromQueryParams(queryParameter: Record<string, unknown>): SearchSessionsQuery {
        return SearchSessionsQuery.fromJS({
            transactionId: queryParameter.transactionId as string | undefined,
            fromDate: queryParameter.fromDate ? dayjs(String(queryParameter.fromDate), "YYYY-MM-DDHH:mm").toString() : undefined,
            toDate: queryParameter.toDate ? dayjs(String(queryParameter.toDate), "YYYY-MM-DDHH:mm").toString() : undefined,
            state: this.toEnum<SessionState>(queryParameter.state, SessionState),
            type: this.toEnum<SessionType>(queryParameter.type, SessionType),
            service: this.toEnum<AthumiService>(queryParameter.service, AthumiService),
            paymentMethod: this.toEnum<PaymentMethod>(queryParameter.paymentMethod, PaymentMethod),
            actorIdentifier: queryParameter.actorIdentifier as string | undefined,
            actorName: queryParameter.actorName as string | undefined,
            pageSize: queryParameter.pageSize ? Number(queryParameter.pageSize) : 50,
            page: queryParameter.page ? Number(queryParameter.page) : 1,
            orderDirection: this.toEnum<SessionType>(queryParameter.orderDirection, OrderDirection),
            sessionDetail: queryParameter.sessionDetail as string | undefined
        });
    }
    
    static compareSearchSessionQueryFromParams(a: SearchSessionsQuery, b: SearchSessionsQuery): boolean {
        return JSON.stringify(SearchSessionsQueryMapper.toQueryParams(a)) === JSON.stringify(SearchSessionsQueryMapper.toQueryParams(b))
    }
}

export class SearchInvoicesQueryMapper {
    static toQueryParams(query: AllInvoicesQuery): Record<string, string | undefined> {
        return {
            fromDate: query.fromDate ? dayjs(query.fromDate).format("YYYY-MM-DD") : undefined,
            toDate: query.toDate ? dayjs(query.toDate).format("YYYY-MM-DD") : undefined,
            docNr: query.docNr ?? undefined,
            service: query.service ?? undefined,
            actorId: query.actorId ?? undefined,
            pageSize: query.pageSize != null ? String(query.pageSize) : undefined,
            page: query.page != null ? String(query.page) : undefined,
        } as Record<string, string | undefined>;
    }

    static fromQueryParams(queryParameter: Record<string, unknown>): AllInvoicesQuery {
        return AllInvoicesQuery.fromJS({
            fromDate: queryParameter.fromDate ? dayjs(String(queryParameter.fromDate), "YYYY-MM-DD").toString() : undefined,
            toDate: queryParameter.toDate ? dayjs(String(queryParameter.toDate), "YYYY-MM-DD").toString() : undefined,
            docNr: queryParameter.docNr as string | undefined,
            service: queryParameter.service ? (queryParameter.service as AthumiService) : undefined,
            actorId: queryParameter.actorId as string | undefined,
            pageSize: queryParameter.pageSize ? Number(queryParameter.pageSize) : 50,
            page: queryParameter.page ? Number(queryParameter.page) : 1,
        });
    }

    static compareSearchSessionQueryFromParams(a: AllInvoicesQuery, b: AllInvoicesQuery): boolean {
        return JSON.stringify(SearchInvoicesQueryMapper.toQueryParams(a)) === JSON.stringify(SearchInvoicesQueryMapper.toQueryParams(b))
    }
}

export class SearchPurchaseInvoicesQueryMapper {
    static toQueryParams(query: AllPurchaseInvoicesQuery): Record<string, string | undefined> {
        return {
            fromDate: query.fromDate ? dayjs(query.fromDate).format("YYYY-MM-DD") : undefined,
            toDate: query.toDate ? dayjs(query.toDate).format("YYYY-MM-DD") : undefined,
            docNr: query.docNr ?? undefined,
            service: query.service ?? undefined,
            pageSize: query.pageSize != null ? String(query.pageSize) : undefined,
            page: query.page != null ? String(query.page) : undefined,
        } as Record<string, string | undefined>;
    }

    static fromQueryParams(queryParameter: Record<string, unknown>): AllPurchaseInvoicesQuery {
        return AllInvoicesQuery.fromJS({
            fromDate: queryParameter.fromDate ? dayjs(String(queryParameter.fromDate), "YYYY-MM-DD").toString() : undefined,
            toDate: queryParameter.toDate ? dayjs(String(queryParameter.toDate), "YYYY-MM-DD").toString() : undefined,
            docNr: queryParameter.docNr as string | undefined,
            service: queryParameter.service ? (queryParameter.service as AthumiService) : undefined,
            actorId: queryParameter.actorId as string | undefined,
            pageSize: queryParameter.pageSize ? Number(queryParameter.pageSize) : 50,
            page: queryParameter.page ? Number(queryParameter.page) : 1,
        });
    }

    static compareSearchSessionQueryFromParams(a: AllPurchaseInvoicesQuery, b: AllPurchaseInvoicesQuery): boolean {
        return JSON.stringify(SearchPurchaseInvoicesQueryMapper.toQueryParams(a)) === JSON.stringify(SearchPurchaseInvoicesQueryMapper.toQueryParams(b))
    }
}

export class SearchOrganisationsQueryMapper {
    static toQueryParams(query: ISearchOrganisationQuery): Record<string, string | undefined> {
        return {
            keyword: query.keyword ?? undefined,
            type: query.type ?? undefined,
        } as Record<string, string | undefined>;
    }

    static fromQueryParams(queryParameter: Record<string, unknown>): ISearchOrganisationQuery {
        return {
            keyword: queryParameter.keyword ?? undefined,
            type: queryParameter.type ?? undefined,
        } as ISearchOrganisationQuery;
    }

    static compareSearchSessionQueryFromParams(a: ISearchOrganisationQuery, b: ISearchOrganisationQuery): boolean {
        return JSON.stringify(SearchOrganisationsQueryMapper.toQueryParams(a)) === JSON.stringify(SearchOrganisationsQueryMapper.toQueryParams(b))
    }
}

export const useSearchQueryStore = defineStore('search-query-store', () => {
    const route = useRoute();
    const router = useRouter();
    
    const syncUrlToQueryIfChanged = async <T>(query: T, mapperClass: { toQueryParams: (q: T) => Record<string, string | undefined> }, replace = true) => {
        const current = route.query;
        const next = mapperClass.toQueryParams(query);
        const same = JSON.stringify(current) === JSON.stringify(next);

        if (!same) {
            if (replace) {
                await router.replace({ query: next });
            } else {
                await router.push({ query: next });
            }
        }
    };
    
    const syncUrlToSearchSessionsQueryIfChanged = async (q: SearchSessionsQuery, replace = true) => {
        return syncUrlToQueryIfChanged(q, SearchSessionsQueryMapper, replace);
    };
    const syncUrlToSearchOrganisationsQueryIfChanged = async (q: ISearchOrganisationQuery, replace = true) => {
        return syncUrlToQueryIfChanged(q, SearchOrganisationsQueryMapper, replace);
    };
    
    const syncUrlToInvoicesQueryIfChanged = async (q: AllInvoicesQuery, replace = true) => {
        return syncUrlToQueryIfChanged(q, SearchInvoicesQueryMapper, replace);
    };

    const syncUrlToPurchaseInvoicesQueryIfChanged = async (q: AllPurchaseInvoicesQuery, replace = true) => {
        return syncUrlToQueryIfChanged(q, SearchPurchaseInvoicesQueryMapper, replace);
    };

    return {
        syncUrlToSearchSessionsQueryIfChanged,
        syncUrlToSearchOrganisationsQueryIfChanged,
        syncUrlToInvoicesQueryIfChanged,
        syncUrlToPurchaseInvoicesQueryIfChanged
    };
});